import * as React from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescriptionHidden,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/custom/searchable-select'
import { useCreateCompany } from '@/hooks/use-companies'
import {
  useSectorsSearch,
  useUsersSearch,
  useContactsSearch,
  useCountriesSearch,
  useStatesSearch,
  useGetOrCreateLocation,
} from '@/hooks/use-reference-data'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { showSuccess, showError } from '@/lib/toast'
import {
  addCompanySchema,
  type AddCompanyFormValues,
} from '@/lib/schemas/company.schema'
import { formatContactName } from '@/lib/contacts/format-contact-name'

interface AddCompanyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function BorderedSection({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-primary/20 p-3">{children}</div>
  )
}

export function AddCompanyModal({ open, onOpenChange }: AddCompanyModalProps) {
  const form = useForm<AddCompanyFormValues>({
    resolver: standardSchemaResolver(addCompanySchema),
    defaultValues: {
      companyName: '',
      externalId: '',
      sectorId: '',
      countryId: '',
      stateId: '',
      primaryOwnerId: '',
      secondaryOwnerId: '',
      primaryContactId: '',
      description: '',
    },
  })

  // Watch country for cascading dropdown logic
  const countryId = form.watch('countryId')

  // Search states
  const [sectorSearch, setSectorSearch] = React.useState('')
  const [countrySearch, setCountrySearch] = React.useState('')
  const [stateSearch, setStateSearch] = React.useState('')
  const [primaryOwnerSearch, setPrimaryOwnerSearch] = React.useState('')
  const [secondaryOwnerSearch, setSecondaryOwnerSearch] = React.useState('')
  const [contactSearch, setContactSearch] = React.useState('')

  // Debounced search values
  const debouncedSectorSearch = useDebouncedValue(sectorSearch, 300)
  const debouncedCountrySearch = useDebouncedValue(countrySearch, 300)
  const debouncedStateSearch = useDebouncedValue(stateSearch, 300)
  const debouncedPrimaryOwnerSearch = useDebouncedValue(primaryOwnerSearch, 300)
  const debouncedSecondaryOwnerSearch = useDebouncedValue(
    secondaryOwnerSearch,
    300
  )
  const debouncedContactSearch = useDebouncedValue(contactSearch, 300)

  // Data queries
  const { data: sectorsData, isLoading: sectorsLoading } = useSectorsSearch({
    search: debouncedSectorSearch,
    enabled: open,
  })
  const { data: countriesData, isLoading: countriesLoading } =
    useCountriesSearch({
      search: debouncedCountrySearch,
      enabled: open,
    })
  const { data: statesData, isLoading: statesLoading } = useStatesSearch({
    countryId: countryId ?? '',
    search: debouncedStateSearch,
    enabled: open && !!countryId,
  })
  const { data: primaryOwnersData, isLoading: primaryOwnersLoading } =
    useUsersSearch({
      search: debouncedPrimaryOwnerSearch,
      enabled: open,
    })
  const { data: secondaryOwnersData, isLoading: secondaryOwnersLoading } =
    useUsersSearch({
      search: debouncedSecondaryOwnerSearch,
      enabled: open,
    })
  const { data: contactsData, isLoading: contactsLoading } = useContactsSearch({
    search: debouncedContactSearch,
    enabled: open,
  })

  // Mutations
  const createCompany = useCreateCompany()
  const getOrCreateLocation = useGetOrCreateLocation()

  // Handle country change - clear state
  const handleCountryChange = (value: string | undefined) => {
    form.setValue('countryId', value ?? '')
    form.setValue('stateId', '')
  }

  const onSubmit = async (values: AddCompanyFormValues) => {
    try {
      let locationId: string | undefined

      // If country is provided, get or create the location
      if (values.countryId) {
        const locationResult = await getOrCreateLocation.mutateAsync({
          countryId: values.countryId,
          stateId: values.stateId || undefined,
        })
        locationId = locationResult.id
      }

      await createCompany.mutateAsync({
        displayName: values.companyName.trim(),
        status: 'ACTIVE',
        externalReference: values.externalId
          ? {
              dataSourceCode: 'CHRONOGRAPH',
              externalId: values.externalId.trim(),
            }
          : undefined,
        sectorIds: values.sectorId ? [values.sectorId] : undefined,
        locationId: locationId,
        primaryOwnerId: values.primaryOwnerId || undefined,
        secondaryOwnerId: values.secondaryOwnerId || undefined,
        primaryContactId: values.primaryContactId || undefined,
        description: values.description?.trim() || undefined,
      })

      showSuccess('Company created successfully')
      onOpenChange(false)
    } catch {
      showError('Failed to create company')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-150 px-4 py-5')}>
        <DialogHeader>
          <DialogTitle className="text-balance">Add New Company</DialogTitle>
          <DialogDescriptionHidden>
            Fill out the form to create a new company
          </DialogDescriptionHidden>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-3">
              {/* Company Name */}
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Company Name<span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Bordered Section: Chronograph ID, Category, Sector */}
              <BorderedSection>
                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="externalId"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>
                          Chronograph ID
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter ID"
                            className="tabular-nums"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sectorId"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>
                          Sector<span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <SearchableSelect
                            options={
                              sectorsData?.data.map((s) => ({
                                value: s.id,
                                label: s.name,
                              })) ?? []
                            }
                            value={field.value}
                            onValueChange={field.onChange}
                            onSearchChange={setSectorSearch}
                            placeholder="Select Sector"
                            isLoading={sectorsLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </BorderedSection>

              {/* Bordered Section: Country, State, City */}
              <BorderedSection>
                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="countryId"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <SearchableSelect
                            options={
                              countriesData?.data.map((c) => ({
                                value: c.id,
                                label: c.name,
                              })) ?? []
                            }
                            value={field.value}
                            onValueChange={handleCountryChange}
                            onSearchChange={setCountrySearch}
                            placeholder="Select Country"
                            isLoading={countriesLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="stateId"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>State</FormLabel>
                        <FormControl>
                          <SearchableSelect
                            options={
                              statesData?.data.map((s) => ({
                                value: s.id,
                                label: s.name,
                              })) ?? []
                            }
                            value={field.value}
                            onValueChange={field.onChange}
                            onSearchChange={setStateSearch}
                            placeholder="Select State"
                            isLoading={statesLoading}
                            disabled={!countryId}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </BorderedSection>

              {/* Bordered Section: Primary Owner, Secondary Owner */}
              <BorderedSection>
                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="primaryOwnerId"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>
                          Primary Owner
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <SearchableSelect
                            options={
                              primaryOwnersData?.data.map((u) => ({
                                value: u.id,
                                label: u.name,
                                description: u.email,
                              })) ?? []
                            }
                            value={field.value}
                            onValueChange={field.onChange}
                            onSearchChange={setPrimaryOwnerSearch}
                            placeholder="Select Team Member"
                            searchPlaceholder="Search Team Member"
                            isLoading={primaryOwnersLoading}
                            showSearchIcon
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="secondaryOwnerId"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Secondary Owner</FormLabel>
                        <FormControl>
                          <SearchableSelect
                            options={
                              secondaryOwnersData?.data.map((u) => ({
                                value: u.id,
                                label: u.name,
                                description: u.email,
                              })) ?? []
                            }
                            value={field.value}
                            onValueChange={field.onChange}
                            onSearchChange={setSecondaryOwnerSearch}
                            placeholder="Select Team Member"
                            searchPlaceholder="Search Team Member"
                            isLoading={secondaryOwnersLoading}
                            showSearchIcon
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </BorderedSection>

              {/* Primary Contact */}
              <FormField
                control={form.control}
                name="primaryContactId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary Contact</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        options={
                          contactsData?.data.map((c) => ({
                            value: c.id,
                            label: formatContactName(c),
                            description: c.email ?? undefined,
                          })) ?? []
                        }
                        value={field.value}
                        onValueChange={field.onChange}
                        onSearchChange={setContactSearch}
                        placeholder="Search Person"
                        searchPlaceholder="Search Person"
                        isLoading={contactsLoading}
                        showSearchIcon
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Short Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter a short description"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Buttons */}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline-muted"
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={createCompany.isPending}
              >
                {createCompany.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
